/**
 * SandboxProcess — represents a running process in the K8s pod.
 *
 * Backed by WebSocket events to/from the SandboxGateway.
 * Uses a sessionId to prevent output from different processes mixing.
 */

import type { Socket } from 'socket.io-client';

let nextSessionId = 1;

export class SandboxProcess {
  private _socket: Socket;
  private _chatId: string;
  private _command: string[];
  private _sessionId: string;
  private _exitResolve: ((code: number) => void) | null = null;
  private _outputHandler: ((data: { sessionId: string; data: string }) => void) | null = null;

  output: ReadableStream<string>;
  input: WritableStream<string>;
  exit: Promise<number>;

  constructor(socket: Socket, chatId: string, command: string[]) {
    this._socket = socket;
    this._chatId = chatId;
    this._command = command;
    this._sessionId = `proc-${nextSessionId++}`;

    // Create output ReadableStream — only accepts events matching this sessionId
    this.output = new ReadableStream<string>({
      start: (controller) => {
        this._outputHandler = (data: { sessionId: string; data: string }) => {
          if (data.sessionId === this._sessionId) {
            controller.enqueue(data.data);
          }
        };

        this._socket.on('terminal:output', this._outputHandler);

        this._socket.once('disconnect', () => {
          if (this._outputHandler) {
            this._socket.off('terminal:output', this._outputHandler);
          }

          try {
            controller.close();
          } catch {
            // already closed
          }
        });
      },
    });

    // Create input WritableStream — DuneaShell calls .getWriter() on this
    this.input = new WritableStream<string>({
      write: (chunk) => {
        this._socket.emit('terminal:input', { sessionId: this._sessionId, data: chunk });
      },
    });

    // Create exit promise
    this.exit = new Promise<number>((resolve) => {
      this._exitResolve = resolve;
    });
  }

  async start(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._socket.emit(
        'terminal:spawn',
        { chatId: this._chatId, command: this._command, sessionId: this._sessionId },
        (response: { success: boolean; error?: string }) => {
          if (response.success) {
            resolve();
          } else {
            reject(new Error(response.error || 'Failed to spawn process'));
          }
        },
      );
    });
  }

  kill(): void {
    this._socket.emit('terminal:input', { sessionId: this._sessionId, data: '\x03' });

    if (this._outputHandler) {
      this._socket.off('terminal:output', this._outputHandler);
      this._outputHandler = null;
    }

    if (this._exitResolve) {
      this._exitResolve(130); // SIGINT exit code
      this._exitResolve = null;
    }
  }

  resize(dimensions: { cols: number; rows: number }): void {
    this._socket.emit('terminal:resize', { sessionId: this._sessionId, ...dimensions });
  }
}
